/**
 * useAuth hook - simplified authentication interface
 *
 * Provides a clean, consistent interface for authentication state
 * across all route modules. Wraps usePermissions with additional
 * convenience methods.
 *
 * Validates: Requirements 3.1, 3.2, 3.3
 */

import { usePermissions } from './usePermissions';
import type { AuthContextValue } from './types';

/**
 * Simplified authentication hook
 *
 * Provides authentication state and methods with a clean interface.
 * This is the primary hook that route modules should use for authentication.
 *
 * @returns AuthContextValue with authentication state and methods
 */
export function useAuth(): AuthContextValue {
  return usePermissions();
}

/**
 * Hook for checking if user is authenticated
 * Convenience hook for components that only need to know auth status
 *
 * @returns boolean indicating if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

/**
 * Hook for checking if authentication is loading
 * Useful for showing loading states during auth initialization
 *
 * @returns boolean indicating if auth state is loading
 */
export function useAuthLoading(): boolean {
  const { isLoading } = useAuth();
  return isLoading;
}
